<template>
  <div class="metadatos-container">
    <!-- Imagen del sistema de metadatos -->
    <div class="header-image">
      <img src="/images/Metadatos-00.jpg" alt="Sistema de Metadatos" class="metadatos-system-image" />
      <h1>Sistema de Administración de Metadatos</h1>
      <p>Gestión eficiente de metadatos del sistema</p>
    </div>

    <!-- Contenedor principal con tabla y botones -->
    <div class="main-content">
      <!-- Header con botones de acción -->
      <div class="table-header">
        <h3>📊 Metadatos del Sistema</h3>
        <div class="header-actions">
          <button class="new-ticket-btn" @click="capturarMetadatos" :disabled="loadingCaptura">
            📸 {{ loadingCaptura ? 'Capturando...' : 'Capturar Metadatos' }}
          </button>
          <button class="stats-btn" @click="mostrarEstadisticas" :disabled="loadingStats">
            📈 {{ loadingStats ? 'Cargando...' : 'Estadísticas' }}
          </button>
        </div>
      </div>

      <!-- Filtros y búsqueda -->
      <div class="filter-section">
        <div class="filter-controls">
          <div class="filter-group">
            <label for="queryFilter">🔍 Buscar metadatos:</label>
            <input 
              id="queryFilter"
              v-model="filtros.query"
              type="text"
              class="filter-input"
              @keyup.enter="buscarMetadatos"
            />
          </div>
          <div class="filter-group">
            <label for="ipFilter">🌐 Filtrar por IP:</label>
            <input 
              id="ipFilter"
              v-model="filtros.ipAgent"
              type="text"
              class="filter-input"
            />
          </div>
          <div class="filter-group">
            <label for="locationFilter">📍 Filtrar por ubicación:</label>
            <input 
              id="locationFilter"
              v-model="filtros.location"
              type="text"
              class="filter-input"
            />
          </div>
          <div class="filter-group">
            <label for="dateFromFilter">📅 Fecha desde:</label>
            <input 
              id="dateFromFilter"
              v-model="filtros.fechaDesde"
              type="date"
              class="filter-input"
            />
          </div>
          <div class="filter-group">
            <label for="dateToFilter">📅 Fecha hasta:</label>
            <input 
              id="dateToFilter"
              v-model="filtros.fechaHasta"
              type="date"
              class="filter-input"
            />
          </div>
          <div class="filter-group">
            <label for="maxFilter">📄 Registros por página:</label>
            <input 
              id="maxFilter"
              v-model="filtros.max"
              type="number"
              min="10"
              max="100"
              class="filter-input"
            />
          </div>
        </div>
        <div class="filter-actions">
          <button class="action-btn" @click="aplicarFiltros" :disabled="loading">
            🔍 {{ loading ? 'Aplicando...' : 'Aplicar Filtros' }}
          </button>
          <button class="action-btn secondary" @click="limpiarFiltros">
            ❌ Limpiar
          </button>
          <button class="action-btn success" @click="exportarDatos" :disabled="loadingExport">
            📥 {{ loadingExport ? 'Exportando...' : 'Exportar' }}
          </button>
        </div>
        <div class="filter-info">
          <span v-if="filtros.query || filtros.ipAgent || filtros.location || filtros.fechaDesde || filtros.fechaHasta">
            Mostrando metadatos filtrados
          </span>
          <span v-else>Mostrando todos los metadatos</span>
          <span class="metadatos-count">({{ totalRegistros }} registros)</span>
        </div>
      </div>

      <!-- Sección de gráficas de análisis -->
      <div class="charts-section">
        <div class="charts-header">
          <h3>📊 Análisis Visual de Accesos</h3>
          <div class="chart-controls">
            <button class="chart-btn" @click="toggleChartView" :class="{ active: showChartView }">
              {{ showChartView ? '📋 Ocultar Gráficas' : '📈 Mostrar Gráficas' }}
            </button>
          </div>
        </div>
        
        <div v-if="showChartView" class="charts-content">
          <!-- Gráfica de distribución de IPs -->
          <div class="chart-container">
            <div class="chart-header">
              <h4>🌐 Distribución de Direcciones IP</h4>
              <div class="chart-legend">
                <div class="legend-item">
                  <span class="legend-dot hospital-ip"></span>
                  <span>Red Hospital (172.x.x.x)</span>
                </div>
                <div class="legend-item">
                  <span class="legend-dot external-ip"></span>
                  <span>IPs Externas</span>
                </div>
              </div>
            </div>
            <div class="chart-body">
              <div class="ip-distribution-chart">
                <div class="chart-stats">
                  <div class="stat-card hospital">
                    <div class="stat-number">{{ estadisticasIP.hospital }}</div>
                    <div class="stat-label">Accesos Hospital</div>
                    <div class="stat-percentage">{{ estadisticasIP.hospitalPercentage }}%</div>
                  </div>
                  <div class="stat-card external">
                    <div class="stat-number">{{ estadisticasIP.external }}</div>
                    <div class="stat-label">Accesos Externos</div>
                    <div class="stat-percentage">{{ estadisticasIP.externalPercentage }}%</div>
                  </div>
                </div>
                <div class="chart-visual">
                  <div class="chart-bars">
                    <div class="bar-container">
                      <div class="bar hospital-bar" :style="{ height: estadisticasIP.hospitalPercentage + '%' }"></div>
                      <div class="bar-label">Hospital</div>
                    </div>
                    <div class="bar-container">
                      <div class="bar external-bar" :style="{ height: estadisticasIP.externalPercentage + '%' }"></div>
                      <div class="bar-label">Externos</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Gráfica de puntos por IP -->
          <div class="chart-container">
            <div class="chart-header">
              <h4>📍 Mapa de Puntos por Dirección IP</h4>
              <div class="chart-info">
                <span>Cada punto representa un acceso desde esa IP</span>
              </div>
            </div>
            <div class="chart-body">
              <div class="ip-points-chart">
                <div class="points-grid">
                  <div 
                    v-for="(ipData, index) in ipPointsData" 
                    :key="index"
                    class="ip-point"
                    :class="ipData.isHospital ? 'hospital-point' : 'external-point'"
                    :title="`IP: ${ipData.ip}\nAccesos: ${ipData.count}\nÚltimo acceso: ${ipData.lastAccess}`"
                    :style="{ 
                      left: ipData.x + '%', 
                      top: ipData.y + '%',
                      transform: `scale(${Math.min(1 + (ipData.count / 10), 2)})`
                    }"
                  >
                    <div class="point-tooltip">
                      <div class="tooltip-ip">{{ ipData.ip }}</div>
                      <div class="tooltip-count">{{ ipData.count }} accesos</div>
                    </div>
                  </div>
                </div>
                <div class="chart-labels">
                  <div class="label-top">Frecuencia de Acceso</div>
                  <div class="label-left">Tipo de Red</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Gráfica temporal -->
          <div class="chart-container">
            <div class="chart-header">
              <h4>⏰ Distribución Temporal de Accesos</h4>
              <div class="time-filter">
                <select v-model="timeFilter" @change="updateTimeChart" class="time-select">
                  <option value="hour">Por Hora</option>
                  <option value="day">Por Día</option>
                  <option value="week">Por Semana</option>
                </select>
              </div>
            </div>
            <div class="chart-body">
              <div class="time-chart">
                <div class="time-bars">
                  <div 
                    v-for="(timeData, index) in timeChartData" 
                    :key="index"
                    class="time-bar-container"
                  >
                    <div class="time-bar" :style="{ height: timeData.height + '%' }"></div>
                    <div class="time-label">{{ timeData.label }}</div>
                    <div class="time-count">{{ timeData.count }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabla de metadatos -->
      <div class="metadatos-list">
        <table>
          <thead>
            <tr>
              <th class="folio-header">ID</th>
              <th>IP Agent</th>
              <th>User Agent</th>
              <th>Timestamp</th>
              <th>Ubicación</th>
              <th>Creado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="7" class="loading-row">
                <div class="loading-spinner">⏳ Cargando metadatos...</div>
              </td>
            </tr>
            <tr v-else-if="metadatos.length === 0">
              <td colspan="7" class="empty-row">
                <div class="empty-message">📭 No hay metadatos disponibles</div>
              </td>
            </tr>
            <tr v-else v-for="item in metadatos" :key="item.id" class="metadato-row">
              <td class="folio-cell">{{ item.id }}</td>
              <td>
                <span class="ip-chip" :class="getColorIPClass(item.ipAgent)">
                  {{ item.ipAgent }}
                </span>
              </td>
              <td class="user-agent-cell" :title="item.userAgent">
                {{ truncateText(item.userAgent, 50) }}
              </td>
              <td class="timestamp-cell">
                <span class="timestamp-chip">
                  {{ formatearFecha(item.timestamp) }}
                </span>
              </td>
              <td>
                <span class="location-chip" :class="item.location ? 'has-location' : 'no-location'">
                  {{ item.location || 'No especificada' }}
                </span>
              </td>
              <td class="date-cell">{{ formatearFecha(item.dateCreated) }}</td>
              <td class="actions-cell">
                <button class="detalle-btn" @click="verDetalles(item)" title="Ver detalles">
                  👁️
                </button>
                <button class="action-btn warning" @click="editarMetadato(item)" title="Editar">
                  ✏️
                </button>
                <button class="action-btn danger" @click="eliminarMetadato(item)" title="Eliminar">
                  🗑️
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        
        <!-- Paginación -->
        <div class="pagination" v-if="totalRegistros > filtros.max">
          <button 
            class="pagination-btn" 
            @click="cambiarPagina(paginaActual - 1)"
            :disabled="paginaActual <= 1"
          >
            ← Anterior
          </button>
          <span class="pagination-info">
            Página {{ paginaActual }} de {{ Math.ceil(totalRegistros / filtros.max) }}
          </span>
          <button 
            class="pagination-btn" 
            @click="cambiarPagina(paginaActual + 1)"
            :disabled="paginaActual >= Math.ceil(totalRegistros / filtros.max)"
          >
            Siguiente →
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de detalles -->
    <v-dialog v-model="dialogDetalles" max-width="800px">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Detalles del Metadato</span>
          <v-btn icon @click="dialogDetalles = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="metadatoSeleccionado">
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                label="IP Agent"
                :value="metadatoSeleccionado.ipAgent"
                readonly
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                label="Timestamp"
                :value="formatearFecha(metadatoSeleccionado.timestamp)"
                readonly
                outlined
                dense
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                label="User Agent"
                :value="metadatoSeleccionado.userAgent"
                readonly
                outlined
                rows="3"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                label="Ubicación"
                :value="metadatoSeleccionado.location || 'No especificada'"
                readonly
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                label="Fecha de Creación"
                :value="formatearFecha(metadatoSeleccionado.dateCreated)"
                readonly
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                label="Última Actualización"
                :value="formatearFecha(metadatoSeleccionado.lastUpdated)"
                readonly
                outlined
                dense
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" @click="dialogDetalles = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de edición -->
    <v-dialog v-model="dialogEdicion" max-width="600px">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Editar Metadato</span>
          <v-btn icon @click="dialogEdicion = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-form ref="formEdicion" v-model="formValido">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="metadatoEdicion.ipAgent"
                  label="IP Agent"
                  :rules="[v => !!v || 'IP Agent es requerido']"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="metadatoEdicion.userAgent"
                  label="User Agent"
                  :rules="[v => !!v || 'User Agent es requerido']"
                  outlined
                  rows="3"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="metadatoEdicion.location"
                  label="Ubicación"
                  outlined
                  dense
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="secondary" @click="dialogEdicion = false">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            @click="guardarEdicion"
            :loading="loadingEdicion"
            :disabled="!formValido"
          >
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de estadísticas -->
    <v-dialog v-model="dialogEstadisticas" max-width="1000px">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Estadísticas de Metadatos</span>
          <v-btn icon @click="dialogEstadisticas = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="estadisticas">
          <v-row>
            <v-col cols="12" md="4">
              <v-card color="primary" dark>
                <v-card-text>
                  <div class="text-h4">{{ estadisticas.totalRegistros }}</div>
                  <div>Total de Registros</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="4">
              <v-card color="success" dark>
                <v-card-text>
                  <div class="text-h4">{{ estadisticas.ipsUnicas }}</div>
                  <div>IPs Únicas</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="4">
              <v-card color="info" dark>
                <v-card-text>
                  <div class="text-h4">{{ estadisticas.ubicacionesUnicas }}</div>
                  <div>Ubicaciones Únicas</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <div class="scatter-wrapper">
            <div class="scatter-heading">
              <h3>Horas con más accesos desde la red hospitalaria</h3>
              <div v-if="horaPicoLabel" class="scatter-highlight">
                Hora pico: <strong>{{ horaPicoLabel }}</strong>
                <span v-if="maxAccesosHora">({{ maxAccesosHora }} accesos)</span>
              </div>
            </div>
            <p class="scatter-subtitle">
              Se consideran las IP internas que comienzan con <code>172.</code> para identificar los picos de actividad dentro del hospital.
            </p>

            <div v-if="scatterHoras.length" class="scatter-chart">
              <div class="scatter-axis-y">
                <span
                  v-for="tick in scatterYAxisLabels"
                  :key="'y-' + tick.value"
                  :style="{ bottom: tick.position + '%' }"
                >
                  {{ tick.label }}
                </span>
              </div>

              <div class="scatter-grid">
                <div
                  v-for="punto in scatterHoras"
                  :key="punto.label"
                  class="scatter-point"
                  :style="{
                    left: punto.x + '%',
                    top: punto.top + '%',
                    width: punto.size + 'px',
                    height: punto.size + 'px'
                  }"
                  :title="`${punto.label} · ${punto.count} accesos`"
                >
                  <div class="scatter-tooltip">
                    {{ punto.label }} · {{ punto.count }} accesos
                  </div>
                </div>
              </div>

              <div class="scatter-axis-x">
                <span
                  v-for="tick in scatterAxisLabels"
                  :key="'x-' + tick.value"
                  :style="{ left: tick.position + '%' }"
                >
                  {{ tick.label }}
                </span>
              </div>
            </div>

            <div v-else class="scatter-empty">
              No hay accesos registrados desde la red hospitalaria en el periodo analizado.
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" @click="dialogEstadisticas = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para notificaciones -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: 'MetadatosAdmin',
  data() {
    return {
      // Estado de carga
      loading: false,
      loadingCaptura: false,
      loadingStats: false,
      loadingEdicion: false,
      loadingExport: false,

      // Datos
      metadatos: [],
      totalRegistros: 0,
      paginaActual: 1,

      // Filtros
      filtros: {
        query: '',
        ipAgent: '',
        location: '',
        fechaDesde: '',
        fechaHasta: '',
        max: 20,
        offset: 0
      },

      // Modales
      dialogDetalles: false,
      dialogEdicion: false,
      dialogEstadisticas: false,

      // Formularios
      formValido: false,
      metadatoSeleccionado: null,
      metadatoEdicion: {
        id: null,
        ipAgent: '',
        userAgent: '',
        location: ''
      },
      estadisticas: null,
      scatterHoras: [],
      horaPicoLabel: '',
      maxAccesosHora: 0,

      // Notificaciones
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',

      // Gráficas
      showChartView: false,
      timeFilter: 'hour',
      estadisticasIP: {
        hospital: 0,
        external: 0,
        hospitalPercentage: 0,
        externalPercentage: 0
      },
      ipPointsData: [],
      timeChartData: [],

      // Headers de la tabla
      headers: [
        { text: 'ID', value: 'id', sortable: false, width: '80px' },
        { text: 'IP Agent', value: 'ipAgent', sortable: true },
        { text: 'User Agent', value: 'userAgent', sortable: false, width: '300px' },
        { text: 'Timestamp', value: 'timestamp', sortable: true },
        { text: 'Ubicación', value: 'location', sortable: true },
        { text: 'Creado', value: 'dateCreated', sortable: true },
        { text: 'Acciones', value: 'actions', sortable: false, width: '150px' }
      ]
    }
  },

  mounted() {
    this.cargarMetadatos()
  },

  computed: {
    scatterAxisLabels() {
      const ticks = [0, 4, 8, 12, 16, 20, 23]
      return ticks.map(hour => ({
        value: hour,
        label: `${hour.toString().padStart(2, '0')}:00`,
        position: 5 + (hour / 23) * 90
      }))
    },
    scatterYAxisLabels() {
      const max = this.maxAccesosHora || 0
      if (max === 0) {
        return [{ value: 0, label: 0, position: 5 }]
      }
      const steps = Math.max(Math.ceil(max / 3), 1)
      const labels = []
      for (let value = 0; value <= max; value += steps) {
        labels.push(value)
      }
      if (labels[labels.length - 1] !== max) {
        labels.push(max)
      }
      return labels.map(value => ({
        value,
        label: value,
        position: (max > 0 ? (value / max) * 80 + 5 : 5)
      }))
    }
  },

  methods: {
    // Cargar metadatos desde el backend
    async cargarMetadatos() {
      this.loading = true
      try {
        // Llamada real al backend de metadatos
        const response = await this.$http.get('/metadatos/metadatos/listar', {
          params: {
            query: this.filtros.query,
            ipAgent: this.filtros.ipAgent,
            location: this.filtros.location,
            fechaDesde: this.filtros.fechaDesde,
            fechaHasta: this.filtros.fechaHasta,
            max: this.filtros.max,
            offset: this.filtros.offset
          }
        })
        
        if (response.data && response.data.root) {
          this.metadatos = response.data.root
          this.totalRegistros = response.data.total || response.data.root.length
          this.mostrarNotificacion('Metadatos cargados correctamente', 'success')
        } else {
          this.metadatos = []
          this.totalRegistros = 0
          this.mostrarNotificacion('No se encontraron metadatos', 'info')
        }
        
      } catch (error) {
        console.error('❌ Error al cargar metadatos:', error)
        this.mostrarNotificacion('Error al cargar metadatos: ' + (error.response?.data?.mensaje || error.message), 'error')
        
        // Sin datos de fallback - mostrar lista vacía
        this.metadatos = []
        this.totalRegistros = 0
      } finally {
        this.loading = false
      }
    },

    // Aplicar filtros
    async aplicarFiltros() {
      this.paginaActual = 1
      this.filtros.offset = 0
      await this.cargarMetadatos()
    },

    // Limpiar filtros
    limpiarFiltros() {
      this.filtros = {
        query: '',
        ipAgent: '',
        location: '',
        fechaDesde: '',
        fechaHasta: '',
        max: 20,
        offset: 0
      }
      this.cargarMetadatos()
    },

    // Buscar metadatos
    async buscarMetadatos() {
      await this.aplicarFiltros()
    },

    // Cambiar página
    async cambiarPagina(pagina) {
      this.paginaActual = pagina
      this.filtros.offset = (pagina - 1) * this.filtros.max
      await this.cargarMetadatos()
    },

    // Cambiar items por página
    async cambiarItemsPorPagina(items) {
      this.filtros.max = items
      this.paginaActual = 1
      this.filtros.offset = 0
      await this.cargarMetadatos()
    },

    // Ver detalles
    verDetalles(metadato) {
      this.metadatoSeleccionado = metadato
      this.dialogDetalles = true
    },

    // Editar metadato
    editarMetadato(metadato) {
      this.metadatoEdicion = {
        id: metadato.id,
        ipAgent: metadato.ipAgent,
        userAgent: metadato.userAgent,
        location: metadato.location || ''
      }
      this.dialogEdicion = true
    },

    // Guardar edición
    async guardarEdicion() {
      if (!this.$refs.formEdicion.validate()) return

      this.loadingEdicion = true
      try {
        // Llamada real al backend para actualizar metadato
        const response = await this.$http.put(`/metadatos/metadatos/actualizar/${this.metadatoEdicion.id}`, {
          ipAgent: this.metadatoEdicion.ipAgent,
          userAgent: this.metadatoEdicion.userAgent,
          location: this.metadatoEdicion.location
        })
        
        if (response.data && response.data.success) {
        this.mostrarNotificacion('Metadato actualizado correctamente', 'success')
        this.dialogEdicion = false
        await this.cargarMetadatos()
        } else {
          this.mostrarNotificacion('Error al actualizar metadato', 'error')
        }
        
      } catch (error) {
        console.error('❌ Error al guardar metadato:', error)
        this.mostrarNotificacion('Error al guardar metadato: ' + (error.response?.data?.mensaje || error.message), 'error')
      } finally {
        this.loadingEdicion = false
      }
    },

    // Eliminar metadato
    async eliminarMetadato(metadato) {
      const confirmacion = await this.$swal.fire({
        title: '¿Eliminar metadato?',
        text: `¿Estás seguro de eliminar el metadato de IP ${metadato.ipAgent}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      })

      if (confirmacion.isConfirmed) {
        try {
          // Llamada real al backend para eliminar metadato
          const response = await this.$http.delete(`/metadatos/metadatos/eliminar/${metadato.id}`)
          
          if (response.data && response.data.success) {
          this.mostrarNotificacion('Metadato eliminado correctamente', 'success')
          await this.cargarMetadatos()
          } else {
            this.mostrarNotificacion('Error al eliminar metadato', 'error')
          }
          
        } catch (error) {
          console.error('❌ Error al eliminar metadato:', error)
          this.mostrarNotificacion('Error al eliminar metadato: ' + (error.response?.data?.mensaje || error.message), 'error')
        }
      }
    },

    // Capturar metadatos
    async capturarMetadatos() {
      this.loadingCaptura = true
      try {
        // Llamada real al backend para capturar metadatos
        const response = await this.$http.post('/metadatos/metadatos/capturar')
        
        if (response.data && response.data.success) {
        this.mostrarNotificacion('Metadatos capturados correctamente', 'success')
        await this.cargarMetadatos()
        } else {
          this.mostrarNotificacion('Error al capturar metadatos', 'error')
        }
        
      } catch (error) {
        console.error('❌ Error al capturar metadatos:', error)
        this.mostrarNotificacion('Error al capturar metadatos: ' + (error.response?.data?.mensaje || error.message), 'error')
      } finally {
        this.loadingCaptura = false
      }
    },

    // Mostrar estadísticas
    async mostrarEstadisticas() {
      this.loadingStats = true
      try {
        const response = await this.$http.get('/metadatos/metadatos/estadisticas')
        const payload = response.data || {}

        if (payload.error) {
          throw new Error(payload.mensaje || 'El backend devolvió un error')
        }

        const stats = payload.estadisticas || payload
        const resumen = this.normalizarResumenEstadisticas(stats)

        if (resumen) {
          this.estadisticas = resumen
          this.generarGraficaAccesosPorHora(stats)
        } else {
          this.estadisticas = this.calcularResumenLocal()
          this.generarGraficaAccesosPorHora()
          this.mostrarNotificacion('No se recibieron estadísticas completas; se muestran datos locales.', 'warning')
        }

        if (!this.scatterHoras.length && this.metadatos.length) {
          this.generarGraficaAccesosPorHora()
        }

        this.dialogEstadisticas = true
      } catch (error) {
        console.error('❌ Error al cargar estadísticas:', error)
        if (this.metadatos.length) {
          this.estadisticas = this.calcularResumenLocal()
          this.generarGraficaAccesosPorHora()
          this.dialogEstadisticas = true
          this.mostrarNotificacion('No se pudieron cargar las estadísticas; se muestran datos calculados localmente.', 'warning')
        } else {
          this.mostrarNotificacion('Error al cargar estadísticas: ' + (error.response?.data?.mensaje || error.message), 'error')
        }
      } finally {
        this.loadingStats = false
      }
    },

    // Exportar datos
    async exportarDatos() {
      this.loadingExport = true
      try {
        // Simular exportación
        await new Promise(resolve => setTimeout(resolve, 2000))
        
        this.mostrarNotificacion('Datos exportados correctamente', 'success')
        
      } catch (error) {
        console.error('❌ Error al exportar datos:', error)
        this.mostrarNotificacion('Error al exportar datos', 'error')
      } finally {
        this.loadingExport = false
      }
    },

    // Utilidades
    formatearFecha(fecha) {
      if (!fecha) return 'N/A'
      return new Date(fecha).toLocaleString('es-MX')
    },

    getColorIP(ip) {
      if (ip.startsWith('192.168.')) return 'primary'
      if (ip.startsWith('10.')) return 'success'
      if (ip.startsWith('172.')) return 'info'
      return 'grey'
    },

    getColorIPClass(ip) {
      if (ip.startsWith('192.168.')) return 'ip-primary'
      if (ip.startsWith('10.')) return 'ip-success'
      if (ip.startsWith('172.')) return 'ip-info'
      return 'ip-grey'
    },

    truncateText(text, maxLength) {
      if (!text) return ''
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
    },

    mostrarNotificacion(texto, color = 'success') {
      this.snackbarText = texto
      this.snackbarColor = color
      this.snackbar = true
    },

    normalizarResumenEstadisticas(stats) {
      if (!stats || typeof stats !== 'object') return null

      const totalRegistros = stats.totalRegistros ?? stats.total ?? stats.totalMetadatos ?? null
      const ipsUnicas = stats.ipsUnicas ?? stats.totalIps ?? stats.ips ?? stats.totalIp ?? null
      const ubicacionesUnicas = stats.ubicacionesUnicas ?? stats.totalUbicaciones ?? stats.ubicaciones ?? null

      const hayDatos = [totalRegistros, ipsUnicas, ubicacionesUnicas].some(valor => valor !== null && valor !== undefined)
      if (!hayDatos) return null

      return {
        totalRegistros: totalRegistros ?? this.totalRegistros ?? 0,
        ipsUnicas: ipsUnicas ?? 0,
        ubicacionesUnicas: ubicacionesUnicas ?? 0
      }
    },

    calcularResumenLocal() {
      const totalRegistros = this.metadatos.length
      const ips = new Set(this.metadatos.map(item => item.ipAgent).filter(Boolean)).size
      const ubicaciones = new Set(this.metadatos.map(item => item.location).filter(Boolean)).size
      return {
        totalRegistros,
        ipsUnicas: ips,
        ubicacionesUnicas: ubicaciones
      }
    },

    obtenerMapaHoras(stats) {
      if (!stats || typeof stats !== 'object') return null
      const candidatos = [
        stats.accesosHospitalPorHora,
        stats.accesosPorHoraHospital,
        stats.distribucionHorasHospital,
        stats.horasHospital,
        stats.porHoraHospital,
        stats.redHospital?.porHora,
        stats.redHospital?.accesos,
        stats.accesosPorHora?.hospital
      ]
      for (const mapa of candidatos) {
        if (mapa && typeof mapa === 'object') {
          return mapa
        }
      }
      return null
    },

    generarGraficaAccesosPorHora(stats = null) {
      const conteoPorHora = Array(24).fill(0)
      const mapaHoras = this.obtenerMapaHoras(stats)

      if (mapaHoras) {
        Object.keys(mapaHoras).forEach(key => {
          const hora = parseInt(key, 10)
          if (Number.isFinite(hora) && hora >= 0 && hora < 24) {
            const valor = mapaHoras[key]
            if (typeof valor === 'number') {
              conteoPorHora[hora] = valor
            } else if (valor && typeof valor === 'object') {
              const posibleNumero = valor.count ?? valor.total ?? valor.valor ?? 0
              conteoPorHora[hora] = Number(posibleNumero) || 0
            }
          }
        })
      } else if (this.metadatos.length) {
        this.metadatos.forEach(item => {
          if (item.ipAgent && item.ipAgent.startsWith('172.')) {
            const fecha = new Date(item.timestamp || item.dateCreated)
            if (!Number.isNaN(fecha.getTime())) {
              const hora = fecha.getHours()
              conteoPorHora[hora] += 1
            }
          }
        })
      }

      const max = Math.max(...conteoPorHora, 0)
      this.maxAccesosHora = max
      this.horaPicoLabel = max > 0 ? `${conteoPorHora.indexOf(max).toString().padStart(2, '0')}:00` : ''

      if (max === 0) {
        this.scatterHoras = []
        return
      }

      this.scatterHoras = conteoPorHora
        .map((count, hora) => {
          if (count === 0) return null
          const proporcion = max > 0 ? count / max : 0
          return {
            label: `${hora.toString().padStart(2, '0')}:00`,
            count,
            hour: hora,
            x: 5 + (hora / 23) * 90,
            top: 95 - proporcion * 80,
            size: 10 + proporcion * 14
          }
        })
        .filter(Boolean)
    },

    // Métodos para gráficas
    toggleChartView() {
      this.showChartView = !this.showChartView
      if (this.showChartView) {
        this.calcularEstadisticasIP()
        this.generarPuntosIP()
        this.generarGraficaTemporal()
      }
    },

    calcularEstadisticasIP() {
      const hospitalIPs = this.metadatos.filter(item => item.ipAgent.startsWith('172.'))
      const externalIPs = this.metadatos.filter(item => !item.ipAgent.startsWith('172.'))
      
      const total = this.metadatos.length
      
      this.estadisticasIP = {
        hospital: hospitalIPs.length,
        external: externalIPs.length,
        hospitalPercentage: total > 0 ? Math.round((hospitalIPs.length / total) * 100) : 0,
        externalPercentage: total > 0 ? Math.round((externalIPs.length / total) * 100) : 0
      }
    },

    generarPuntosIP() {
      // Agrupar IPs y contar accesos
      const ipGroups = {}
      this.metadatos.forEach(item => {
        if (!ipGroups[item.ipAgent]) {
          ipGroups[item.ipAgent] = {
            ip: item.ipAgent,
            count: 0,
            lastAccess: item.timestamp,
            isHospital: item.ipAgent.startsWith('172.')
          }
        }
        ipGroups[item.ipAgent].count++
        if (new Date(item.timestamp) > new Date(ipGroups[item.ipAgent].lastAccess)) {
          ipGroups[item.ipAgent].lastAccess = item.timestamp
        }
      })

      // Convertir a array y generar posiciones aleatorias para el mapa de puntos
      this.ipPointsData = Object.values(ipGroups).map(ipData => ({
        ...ipData,
        x: Math.random() * 80 + 10, // Entre 10% y 90%
        y: Math.random() * 80 + 10, // Entre 10% y 90%
        lastAccess: this.formatearFecha(ipData.lastAccess)
      }))
    },

    generarGraficaTemporal() {
      const now = new Date()
      let timeData = []

      if (this.timeFilter === 'hour') {
        // Últimas 24 horas
        for (let i = 23; i >= 0; i--) {
          const hour = new Date(now.getTime() - i * 60 * 60 * 1000)
          const hourStart = new Date(hour.getFullYear(), hour.getMonth(), hour.getDate(), hour.getHours())
          const hourEnd = new Date(hourStart.getTime() + 60 * 60 * 1000)
          
          const count = this.metadatos.filter(item => {
            const itemDate = new Date(item.timestamp)
            return itemDate >= hourStart && itemDate < hourEnd
          }).length

          timeData.push({
            label: hour.getHours().toString().padStart(2, '0') + ':00',
            count: count,
            height: Math.max((count / Math.max(...timeData.map(t => t.count), 1)) * 100, 5)
          })
        }
      } else if (this.timeFilter === 'day') {
        // Últimos 7 días
        for (let i = 6; i >= 0; i--) {
          const day = new Date(now.getTime() - i * 24 * 60 * 60 * 1000)
          const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate())
          const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000)
          
          const count = this.metadatos.filter(item => {
            const itemDate = new Date(item.timestamp)
            return itemDate >= dayStart && itemDate < dayEnd
          }).length

          timeData.push({
            label: day.toLocaleDateString('es-MX', { weekday: 'short' }),
            count: count,
            height: Math.max((count / Math.max(...timeData.map(t => t.count), 1)) * 100, 5)
          })
        }
      } else if (this.timeFilter === 'week') {
        // Últimas 4 semanas
        for (let i = 3; i >= 0; i--) {
          const weekStart = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000)
          const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000)
          
          const count = this.metadatos.filter(item => {
            const itemDate = new Date(item.timestamp)
            return itemDate >= weekStart && itemDate < weekEnd
          }).length

          timeData.push({
            label: `Sem ${4-i}`,
            count: count,
            height: Math.max((count / Math.max(...timeData.map(t => t.count), 1)) * 100, 5)
          })
        }
      }

      this.timeChartData = timeData
    },

    updateTimeChart() {
      this.generarGraficaTemporal()
    }
  }
}
</script>

<style scoped>
/* ===== DISEÑO BASADO EN EL SISTEMA DE TICKETS ===== */

.metadatos-container {
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 10px 0;
  margin: 0;
  font-family: 'Montserrat', sans-serif;
  box-sizing: border-box;
}

/* Header con imagen */
.header-image {
  text-align: center;
  margin-bottom: 30px;
  background: white;
  padding: 30px 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  max-width: 1400px;
  width: 95%;
  box-sizing: border-box;
  overflow: hidden;
}

.metadatos-system-image {
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 20px;
}

.header-image h1 {
  color: #009a44;
  margin: 0 0 10px 0;
  font-size: 1.8rem;
  font-weight: bold;
}

.header-image p {
  color: #6c757d;
  margin: 0;
  font-size: 1rem;
}

/* Contenedor principal */
.main-content {
  width: 95%;
  max-width: 1400px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

/* Header de la tabla */
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.table-header h3 {
  margin: 0;
  color: #009a44;
  font-size: 1.3rem;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 15px;
}

/* Botones principales */
.new-ticket-btn {
  background: #009a44;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.new-ticket-btn:hover:not(:disabled) {
  background: #007a36;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 154, 68, 0.3);
}

.new-ticket-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
  opacity: 0.7;
}

.stats-btn {
  background: #17a2b8;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.stats-btn:hover:not(:disabled) {
  background: #138496;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(23, 162, 184, 0.3);
}

.stats-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
  opacity: 0.7;
}

/* Sección de filtros */
.filter-section {
  padding: 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.filter-controls {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 15px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.filter-group label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #495057;
}

.filter-input {
  padding: 10px 12px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.filter-input:focus {
  outline: none;
  border-color: #009a44;
  box-shadow: 0 0 0 2px rgba(0, 154, 68, 0.1);
}

.filter-actions {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.action-btn {
  background: #009a44;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
}

.action-btn:hover:not(:disabled) {
  background: #007a36;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 154, 68, 0.2);
}

.action-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
  opacity: 0.7;
}

.action-btn.secondary {
  background: #6c757d;
}

.action-btn.secondary:hover:not(:disabled) {
  background: #5a6268;
}

.action-btn.success {
  background: #28a745;
}

.action-btn.success:hover:not(:disabled) {
  background: #218838;
}

.action-btn.warning {
  background: #ffc107;
  color: #212529;
}

.action-btn.warning:hover:not(:disabled) {
  background: #e0a800;
}

.action-btn.danger {
  background: #dc3545;
}

.action-btn.danger:hover:not(:disabled) {
  background: #c82333;
}

.filter-info {
  font-size: 0.85rem;
  color: #6c757d;
  display: flex;
  align-items: center;
  gap: 10px;
}

.metadatos-count {
  font-weight: 600;
  color: #009a44;
}

/* Tabla de metadatos */
.metadatos-list {
  overflow-x: auto;
}

.metadatos-list table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.metadatos-list th {
  background: #f8f9fa;
  padding: 12px 8px;
  font-weight: 600;
  color: #495057;
  text-align: left;
  border-bottom: 2px solid #e9ecef;
  white-space: nowrap;
}

.metadatos-list td {
  padding: 12px 8px;
  border-bottom: 1px solid #e9ecef;
  vertical-align: middle;
}

.metadatos-list tr:hover {
  background: #f8f9fa;
}

.folio-header {
  width: 80px;
  min-width: 80px;
}

.folio-cell {
  font-weight: 600;
  color: #009a44;
  text-align: center;
}

/* Chips de IP */
.ip-chip {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  color: white;
}

.ip-primary {
  background: #007bff;
}

.ip-success {
  background: #28a745;
}

.ip-info {
  background: #17a2b8;
}

.ip-grey {
  background: #6c757d;
}

/* User Agent */
.user-agent-cell {
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Timestamp */
.timestamp-chip {
  background: #e3f2fd;
  color: #1976d2;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Ubicación */
.location-chip {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.location-chip.has-location {
  background: #d4edda;
  color: #155724;
}

.location-chip.no-location {
  background: #f8d7da;
  color: #721c24;
}

/* Fecha */
.date-cell {
  font-size: 0.85rem;
  color: #6c757d;
}

/* Acciones */
.actions-cell {
  white-space: nowrap;
}

.detalle-btn {
  background: #17a2b8;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-right: 5px;
}

.detalle-btn:hover {
  background: #138496;
  transform: translateY(-1px);
}

/* Estados de carga y vacío */
.loading-row, .empty-row {
  text-align: center;
  padding: 40px;
}

.loading-spinner, .empty-message {
  font-size: 1rem;
  color: #6c757d;
}

/* Paginación */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.pagination-btn {
  background: #009a44;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #007a36;
  transform: translateY(-1px);
}

.pagination-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
  opacity: 0.7;
}

.pagination-info {
  font-size: 0.9rem;
  color: #495057;
  font-weight: 600;
}

/* Responsive */
@media (max-width: 768px) {
  .header-image, .main-content {
    width: 98%;
    max-width: 600px;
  }
  
  .filter-controls {
    grid-template-columns: 1fr;
  }
  
  .table-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  
  .header-actions {
    justify-content: center;
  }
  
  .filter-actions {
    flex-direction: column;
  }
  
  .metadatos-list table {
    font-size: 0.8rem;
  }
  
  .metadatos-list th,
  .metadatos-list td {
    padding: 8px 4px;
  }
  
  .pagination {
    flex-direction: column;
    gap: 10px;
  }
}

@media (max-width: 480px) {
  .header-image, .main-content {
    width: 100%;
    margin: 5px;
  }
  
  .metadatos-container {
    padding: 5px 0;
  }
  
  .table-header {
    padding: 10px;
  }
  
  .metadatos-list {
    padding: 10px;
  }
  
  .metadatos-system-image {
    height: 80px;
  }
  
  .header-image h1 {
    font-size: 1.5rem;
  }
}

/* ===== ESTILOS PARA GRÁFICAS ===== */

.charts-section {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.charts-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
}

.charts-header h3 {
  margin: 0;
  color: #009a44;
  font-size: 1.2rem;
  font-weight: 600;
}

.chart-controls {
  display: flex;
  gap: 10px;
}

.chart-btn {
  background: #009a44;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
}

.chart-btn:hover {
  background: #007a36;
  transform: translateY(-1px);
}

.chart-btn.active {
  background: #dc3545;
}

.chart-btn.active:hover {
  background: #c82333;
}

.charts-content {
  padding: 20px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}

.chart-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.chart-header h4 {
  margin: 0;
  color: #495057;
  font-size: 1rem;
  font-weight: 600;
}

.chart-legend {
  display: flex;
  gap: 20px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #6c757d;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.legend-dot.hospital-ip {
  background: #28a745;
}

.legend-dot.external-ip {
  background: #dc3545;
}

.chart-info {
  font-size: 0.8rem;
  color: #6c757d;
}

.chart-body {
  padding: 20px;
}

/* Gráfica de distribución de IPs */
.ip-distribution-chart {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  align-items: center;
}

.chart-stats {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.stat-card {
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  color: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.stat-card.hospital {
  background: linear-gradient(135deg, #28a745, #20c997);
}

.stat-card.external {
  background: linear-gradient(135deg, #dc3545, #e83e8c);
}

.stat-number {
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 0.9rem;
  opacity: 0.9;
  margin-bottom: 5px;
}

.stat-percentage {
  font-size: 1.2rem;
  font-weight: 600;
}

.chart-visual {
  display: flex;
  justify-content: center;
  align-items: end;
  height: 200px;
}

.chart-bars {
  display: flex;
  gap: 20px;
  align-items: end;
  height: 100%;
}

.bar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.bar {
  width: 40px;
  border-radius: 4px 4px 0 0;
  transition: all 0.3s ease;
  min-height: 20px;
}

.hospital-bar {
  background: linear-gradient(to top, #28a745, #20c997);
}

.external-bar {
  background: linear-gradient(to top, #dc3545, #e83e8c);
}

.bar-label {
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 600;
}

/* Gráfica de puntos por IP */
.ip-points-chart {
  position: relative;
  height: 300px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 8px;
  overflow: hidden;
}

.points-grid {
  position: relative;
  width: 100%;
  height: 100%;
}

.ip-point {
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 2;
}

.ip-point:hover {
  transform: scale(1.5) !important;
  z-index: 10;
}

.hospital-point {
  background: #28a745;
  box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.3);
}

.external-point {
  background: #dc3545;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.3);
}

.point-tooltip {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.ip-point:hover .point-tooltip {
  opacity: 1;
}

.tooltip-ip {
  font-weight: 600;
  margin-bottom: 2px;
}

.tooltip-count {
  opacity: 0.8;
}

.chart-labels {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.label-top {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 600;
}

.label-left {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%) rotate(-90deg);
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 600;
}

/* Gráfica temporal */
.time-filter {
  display: flex;
  align-items: center;
  gap: 10px;
}

.time-select {
  padding: 6px 10px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  font-size: 0.8rem;
  background: white;
}

.time-chart {
  height: 200px;
  display: flex;
  align-items: end;
  gap: 8px;
  padding: 10px 0;
}

.time-bars {
  display: flex;
  gap: 8px;
  align-items: end;
  height: 100%;
  width: 100%;
}

.time-bar-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  height: 100%;
}

.time-bar {
  width: 100%;
  background: linear-gradient(to top, #009a44, #20c997);
  border-radius: 2px 2px 0 0;
  min-height: 5px;
  transition: all 0.3s ease;
}

.time-label {
  font-size: 0.7rem;
  color: #6c757d;
  font-weight: 600;
  text-align: center;
}

.time-count {
  font-size: 0.7rem;
  color: #009a44;
  font-weight: 600;
}

/* Responsive para gráficas */
@media (max-width: 768px) {
  .charts-content {
    padding: 15px;
    gap: 15px;
  }
  
  .ip-distribution-chart {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .chart-stats {
    flex-direction: row;
  }
  
  .stat-card {
    flex: 1;
    padding: 15px;
  }
  
  .stat-number {
    font-size: 2rem;
  }
  
  .chart-bars {
    gap: 15px;
  }
  
  .bar {
    width: 30px;
  }
  
  .ip-points-chart {
    height: 250px;
  }
  
  .time-chart {
    height: 150px;
  }
  
  .time-bars {
    gap: 4px;
  }
  
  .time-label {
    font-size: 0.6rem;
  }
  
  .time-count {
    font-size: 0.6rem;
  }
}
.scatter-wrapper {
  margin-top: 32px;
  padding-top: 12px;
  border-top: 1px solid #e3e9f5;
}

.scatter-heading {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.scatter-heading h3 {
  margin: 0;
  font-weight: 700;
  color: #263238;
}

.scatter-highlight {
  font-size: 0.95rem;
  background: #e8f5e9;
  color: #1b5e20;
  padding: 6px 12px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: inset 0 0 0 1px rgba(27, 94, 32, 0.12);
}

.scatter-subtitle {
  margin: 12px 0 20px;
  color: #607d8b;
  font-size: 0.9rem;
}

.scatter-chart {
  position: relative;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(246, 248, 251, 0.96) 100%);
  border: 1px solid #dde3f0;
  border-radius: 16px;
  height: 260px;
  overflow: hidden;
}

.scatter-grid {
  position: absolute;
  left: 60px;
  right: 40px;
  top: 20px;
  bottom: 56px;
  background-image:
    linear-gradient(to right, rgba(102, 126, 234, 0.1) 1px, transparent 1px),
    linear-gradient(to top, rgba(102, 126, 234, 0.08) 1px, transparent 1px);
  background-size: 12.5% 100%, 100% 25%;
  background-position: left top;
}

.scatter-axis-y {
  position: absolute;
  left: 18px;
  top: 20px;
  bottom: 56px;
  width: 40px;
}

.scatter-axis-y span {
  position: absolute;
  right: 0;
  transform: translateY(50%);
  font-size: 0.75rem;
  color: #607d8b;
}

.scatter-axis-x {
  position: absolute;
  left: 60px;
  right: 40px;
  bottom: 18px;
  height: 20px;
}

.scatter-axis-x span {
  position: absolute;
  transform: translateX(-50%);
  font-size: 0.75rem;
  color: #607d8b;
}

.scatter-point {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(135deg, #00a94f 0%, #00843d 100%);
  transform: translate(-50%, -50%);
  box-shadow: 0 8px 18px rgba(0, 169, 79, 0.28);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.scatter-point:hover {
  transform: translate(-50%, -50%) scale(1.1);
  box-shadow: 0 12px 26px rgba(0, 169, 79, 0.35);
}

.scatter-tooltip {
  position: absolute;
  bottom: 110%;
  left: 50%;
  transform: translateX(-50%);
  background: #1b5e20;
  color: white;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 0.75rem;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);
}

.scatter-point:hover .scatter-tooltip {
  opacity: 1;
}

.scatter-empty {
  margin: 16px 0;
  padding: 16px;
  border-radius: 12px;
  background: #f1f4f8;
  color: #607d8b;
  text-align: center;
  font-size: 0.9rem;
}

.scatter-subtitle code {
  background: rgba(33, 150, 243, 0.08);
  color: #1565c0;
  padding: 2px 6px;
  border-radius: 6px;
  font-size: 0.85rem;
}
@media (max-width: 480px) {
  .charts-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }

  .chart-controls {
    justify-content: center;
  }

  .chart-stats {
    flex-direction: column;
  }

  .chart-legend {
    flex-direction: column;
    gap: 10px;
  }

  .ip-points-chart {
    height: 200px;
  }

  .time-chart {
    height: 120px;
  }
}
</style>
